import axios from "axios";
import { useState, useEffect } from "react";
import { useDateFilter } from "./_40b_date_filter_context.js";
import { useLocation } from "react-router-dom";
import { useModuleVisibility, getActiveModule } from "./useModuleVisibility.js";
import { FullscreenIcon, getFullscreenContainerStyles, getFullscreenHeaderStyles, getFullscreenContentStyles, useFullscreenAttribute } from "./fullscreenComponentUtils.js";

const MODULE_NAME = "_57_note_group";

// Tooltip Component
function Tooltip({ text }) {
    return (
        <span
            style={{
                marginLeft: 4,
                verticalAlign: "middle",
                cursor: "pointer",
                color: "#888",
                fontSize: 14
            }}
            title={text}
        >
            🛈
        </span>
    );
}

// Frontend sanitization functions (matching backend)
function sanitizeInput(textInput, maxLength = 5000) {
    if (!textInput || typeof textInput !== "string") {
        return "";
    }

    // Truncate to max length
    let result = textInput.substring(0, maxLength);

    // Remove null bytes
    result = result.replace(/\x00/g, "");

    // Remove control characters (except newlines, tabs, carriage returns)
    result = result.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");

    // Remove emojis and other unicode symbols
    // Keep: alphanumeric, whitespace, common punctuation and symbols
    result = result.replace(/[^\w\s\-./,!?;:()\[\]{}"'@#$%&*+=<>~`^|\\]/gu, "");

    // Remove multiple consecutive spaces
    result = result.replace(/ +/g, " ");

    // Strip leading/trailing whitespace
    result = result.trim();

    return result;
}

export default function NoteGroup() {
    const { navigationDate } = useDateFilter();
    const location = useLocation();

    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState(false);
    const [editIdx, setEditIdx] = useState(null);
    const [editText, setEditText] = useState("");
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState(null);
    const [sanitizationWarning, setSanitizationWarning] = useState(null);
    const [tooltip, setTooltip] = useState("");

    // Check visibility based on module_name URL parameter
    const isVisible = useModuleVisibility(MODULE_NAME);
    const isFullscreen = getActiveModule() === MODULE_NAME;

    // Use centralized hook for fullscreen attribute
    useFullscreenAttribute(isFullscreen);

    // Determine effective date with fallback chain (matching _4_today_trade_table.js pattern)
    const queryParams = new URLSearchParams(location.search);
    let dateFromQuery = queryParams.get("date");
    if (!dateFromQuery || !/^\d{4}-\d{2}-\d{2}$/.test(dateFromQuery)) {
        dateFromQuery = null;
    }
    const effectiveDate =
        (navigationDate && /^\d{4}-\d{2}-\d{2}$/.test(navigationDate) ? navigationDate : null) ||
        (dateFromQuery ? dateFromQuery : null) ||
        (sessionStorage.getItem("navigationDate") && /^\d{4}-\d{2}-\d{2}$/.test(sessionStorage.getItem("navigationDate")) ? sessionStorage.getItem("navigationDate") : null) ||
        (sessionStorage.getItem("latest_date") && /^\d{4}-\d{2}-\d{2}$/.test(sessionStorage.getItem("latest_date")) ? sessionStorage.getItem("latest_date") : null) ||
        new Date().toISOString().slice(0, 10);

    // Fetch notes
    useEffect(() => {
        setLoading(true);
        setError(null);
            
        if (typeof window !== 'undefined' && window.Printdebug) {
            console.log(`[${MODULE_NAME}] Fetching notes for date:`, effectiveDate);
        }
        
        axios.get(`${process.env.REACT_APP_API_URL}/api/note-group`, {
            params: { date: effectiveDate },
            withCredentials: true
        })
            .then(res => {
                if (typeof window !== 'undefined' && window.Printdebug) {
                    console.log(`[${MODULE_NAME}] Notes received:`, res.data);
                }
                
                // Normalize notes: convert null to empty string for display
                const normalizedNotes = res.data.recent_notes ? res.data.recent_notes.map(note => ({
                    ...note,
                    notes: note.notes || "" // Convert null/undefined to empty string
                })).slice(0, 3) : [];
                
                setNotes(normalizedNotes);
                setTooltip(res.data.tooltip || "57 - Last 3 Notes: Displays your most recent journal entries for easy reference.");
                setLoading(false);
            })
            .catch(err => {
                console.error(`[${MODULE_NAME}] Error fetching notes:`, err);
                setError("Failed to load notes");
                setTooltip("57 - Last 3 Notes: Displays your most recent journal entries for easy reference.");
                setLoading(false);
            });
    }, [effectiveDate, location.search]);

    const convertMMDDYYtoYYYYMMDD = (dateStr) => {
        const [month, day, year] = dateStr.split("/");
        const fullYear = year.length === 2 ? (parseInt(year) < 50 ? "20" + year : "19" + year) : year;
        return `${fullYear}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    };

    const displayNotes = notes.length > 0 ? notes : [
        { date: new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }), notes: "", tags: [] },
        { date: new Date(Date.now() - 86400000).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }), notes: "", tags: [] },
        { date: new Date(Date.now() - 172800000).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }), notes: "", tags: [] }
    ];

    const handleDoubleClick = (idx) => {
        if (typeof window !== 'undefined' && window.Printdebug) {
            console.log(`[${MODULE_NAME}] Double-clicked note:`, idx, displayNotes[idx]);
        }
        setEditIdx(idx);
        setEditText(displayNotes[idx].notes || "");
        setUpdating(true);
        setSanitizationWarning(null);
    };

    const handleUpdate = async (idx) => {
        const note = displayNotes[idx];
        setUpdating(false);
        setLoading(true);
        setError(null);
        setSanitizationWarning(null);

        try {
            const sanitizedText = sanitizeInput(editText);

            if (sanitizedText !== editText) {
                setSanitizationWarning("⚠️ Some invalid characters were removed from your note.");
            }

            const isoDate = convertMMDDYYtoYYYYMMDD(note.date);
            
            if (typeof window !== 'undefined' && window.Printdebug) {
                console.log(`[${MODULE_NAME}] Updating note for:`, isoDate, sanitizedText);
            }
            
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/note-group`,
                {
                    date: isoDate,
                    notes: sanitizedText,
                    tags: note.tags || []
                },
                { withCredentials: true }
            );
            
            // Update local state
            const updatedNotes = [...notes];
            updatedNotes[idx] = { ...note, notes: sanitizedText };
            setNotes(updatedNotes);
            
            if (typeof window !== 'undefined' && window.Printdebug) {
                console.log(`[${MODULE_NAME}] Note updated successfully`);
            }
        } catch (err) {
            console.error(`[${MODULE_NAME}] Error updating note:`, err);
            setError(err.response?.data?.message || "Failed to update note");
        }
        setEditIdx(null);
        setLoading(false);
    };

    const handleAddNewNote = async (idx) => {
        const emptyNote = displayNotes[idx];
        setUpdating(false);
        setLoading(true);
        setError(null);
        setSanitizationWarning(null);

        try {
            const sanitizedText = sanitizeInput(editText);

            if (sanitizedText !== editText) {
                setSanitizationWarning("⚠️ Some invalid characters were removed from your note.");
            }

            const isoDate = convertMMDDYYtoYYYYMMDD(emptyNote.date);
            
            if (typeof window !== 'undefined' && window.Printdebug) {
                console.log(`[${MODULE_NAME}] Creating new note for:`, isoDate, sanitizedText);
            }
            
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/note-group`,
                {
                    date: isoDate,
                    notes: sanitizedText,
                    tags: []
                },
                { withCredentials: true }
            );
            
            // Update local state
            const updatedNotes = [...notes];
            updatedNotes[idx] = { ...emptyNote, notes: sanitizedText };
            setNotes(updatedNotes);
            
            if (typeof window !== 'undefined' && window.Printdebug) {
                console.log(`[${MODULE_NAME}] Note created successfully`);
            }
        } catch (err) {
            console.error(`[${MODULE_NAME}] Error creating note:`, err);
            setError(err.response?.data?.message || "Failed to create note");
        }
        setEditIdx(null);
        setLoading(false);
    };

    const handleTextareaInteraction = () => {
        if (editText === "waiting for a great comment") {
            setEditText("");
        }
    };

    const handleTextChange = (e) => {
        const newText = e.target.value;
        setEditText(newText);

        const sanitized = sanitizeInput(newText);
        if (sanitized !== newText && newText.length > sanitized.length) {
            setSanitizationWarning("⚠️ Invalid characters will be removed when saved.");
        } else {
            setSanitizationWarning(null);
        }
    };

    const handleCancel = () => {
        setEditIdx(null);
        setEditText("");
        setSanitizationWarning(null);
    };

    // Only return null AFTER all hooks and logic are complete
    if (!isVisible) return null;

    return (
        <div style={getFullscreenContainerStyles(isFullscreen)} data-analytics={MODULE_NAME}>
            <div style={getFullscreenHeaderStyles(isFullscreen)}>
                <h4 
                    className="collapse-toggle-headline" 
                    onClick={() => setCollapsed(!collapsed)}
                    style={{ margin: 0, flex: 1, cursor: "pointer" }}
                    title="Click to collapse/expand"
                >
                    📝 Notes
                    <Tooltip text={tooltip} />
                </h4>
                {!isFullscreen && (
                    <FullscreenIcon 
                        moduleName={MODULE_NAME}
                        title="Notes"
                        additionalParams={{ date: effectiveDate }}
                    />
                )}
            </div>
            {!collapsed && (
                <div style={{
                    maxHeight: "none",
                    opacity: 1,
                    overflow: "visible",
                    marginTop: 12,
                    paddingTop: 12,
                    display: "block"
                }}>
                    {error && <div style={{ color: "#f87171", marginBottom: 12, padding: 8, backgroundColor: "#3a1e1e", borderRadius: 4 }}>{error}</div>}
                    {loading ? (
                        <div style={{ padding: 12, color: "#94a3b8" }}>Loading notes...</div>
                    ) : (
                        <div>
                            {displayNotes.length === 0 ? (
                                <div style={{ padding: 12, color: "#94a3b8" }}>No notes available</div>
                            ) : (
                                displayNotes.map((note, idx) => (
                                    <div key={idx} style={{ marginBottom: 12, padding: 10, backgroundColor: "#1a1d25", borderRadius: 4 }}>
                                        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6, fontWeight: 600 }}>{note.date}</div>
                                        {editIdx === idx ? (
                                            <div>
                                                <textarea
                                                    autoFocus
                                                    value={editText}
                                                    onChange={handleTextChange}
                                                    onFocus={handleTextareaInteraction}
                                                    placeholder="Enter your notes here..."
                                                    style={{
                                                        width: "100%",
                                                        minHeight: 100,
                                                        padding: 8,
                                                        backgroundColor: "#0f172a",
                                                        color: "#e2e8f0",
                                                        border: "2px solid #3b82f6",
                                                        borderRadius: 4,
                                                        fontFamily: "inherit",
                                                        fontSize: 13,
                                                        resize: "vertical",
                                                        boxSizing: "border-box"
                                                    }}
                                                />
                                                {sanitizationWarning && <div style={{ color: "#fbbf24", fontSize: 12, marginTop: 6 }}>{sanitizationWarning}</div>}
                                                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                                                    <button
                                                        onClick={() => editText.trim() ? (note.notes ? handleUpdate(idx) : handleAddNewNote(idx)) : alert("Please enter a note")}
                                                        style={{
                                                            padding: "8px 16px",
                                                            backgroundColor: "#3b82f6",
                                                            color: "#fff",
                                                            border: "none",
                                                            borderRadius: 4,
                                                            cursor: "pointer",
                                                            fontSize: 13,
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={handleCancel}
                                                        style={{
                                                            padding: "8px 16px",
                                                            backgroundColor: "#6b7280",
                                                            color: "#fff",
                                                            border: "none",
                                                            borderRadius: 4,
                                                            cursor: "pointer",
                                                            fontSize: 13
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                onDoubleClick={() => handleDoubleClick(idx)}
                                                style={{
                                                    minHeight: 60,
                                                    padding: 8,
                                                    backgroundColor: "#0f172a",
                                                    borderRadius: 4,
                                                    color: note.notes && note.notes.trim() ? "#e2e8f0" : "#6b7280",
                                                    cursor: "pointer",
                                                    fontSize: 13,
                                                    wordBreak: "break-word",
                                                    userSelect: "none",
                                                    border: "1px solid #2d3748"
                                                }}
                                                onMouseEnter={(e) => e.target.style.border = "1px solid #475569"}
                                                onMouseLeave={(e) => e.target.style.border = "1px solid #2d3748"}
                                            >
                                                {note.notes && note.notes.trim() ? note.notes : "Double-click to add a note..."}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

